/**
 * The graphics library's wire shapes (ACP-D45, GFX-D107; docs/gfx-m1/plan.md §3): the gfx designer's projects, the
 * tenant's content objects (scene JSON and asset bytes, by SHA-256, in `acp-<stage>-content`) and the tenant's
 * aspect-class set. Cloud only in M1 (no node serves them, no site replicates them).
 *
 * **A project mirrors the designer's `Project`** (`gfx/apps/designer/src/project/types.ts`) with each scene's JSON
 * text — and its sample data track, when it has one — replaced by the SHA-256 of a content object: a save uploads
 * the new objects first (`POST /graphics/content/uploads`), then names them. The tenant is the designer's one cloud
 * workspace, so a project carries no workspace id. `revision` is the optimistic-concurrency counter (D35): a save
 * names the revision it started from (`baseRevision`), and another save since is `conflict`.
 *
 * **Content objects** are immutable and content-addressed (`tenants/<tenantId>/gfx/<sha256>`, never shared across
 * tenants). The cloud hands out presigned S3 URLs: a PUT bound to the object's SHA-256 (S3 verifies the signed
 * `x-amz-checksum-sha256`) and its size (the signed `content-length`), and a GET. Limits are gfx's
 * (`gfx/packages/gfxpkg/src/assets.ts`): 256 MiB per object, 1 GiB of assets per project.
 *
 * **Aspect classes** (GFX-D100) are the gfx SDK's `AspectClass`; the cloud checks their shape and unique ids, the
 * designer checks their semantics (`classSetError`) before it saves.
 */
import { z } from 'zod';
import { entityIdWireSchema, isoTimestampWireSchema, sha256HexWireSchema } from './primitives.js';

/** Most bytes of one content object (gfx's `MAX_ASSET_BYTES`, 256 MiB). */
export const MAX_GRAPHICS_OBJECT_BYTES = 256 * 1024 * 1024;
/** Most bytes of all assets of one project (gfx's `MAX_PACKAGE_BYTES`, 1 GiB). */
export const MAX_GRAPHICS_PROJECT_ASSET_BYTES = 1024 * 1024 * 1024;
/** Longest asset path, in Unicode scalar values (gfx's `MAX_ASSET_PATH`). */
export const MAX_GRAPHICS_ASSET_PATH = 256;
/** Most scenes of one project; the request body's 256 KiB cap is the practical bound well before. */
export const MAX_GRAPHICS_SCENES = 500;
/** Most assets of one project. */
export const MAX_GRAPHICS_ASSETS = 2000;
/** Most objects one upload or download request names. */
export const MAX_GRAPHICS_OBJECTS_PER_REQUEST = 500;
/** Most classes in an aspect-class set. */
export const MAX_GRAPHICS_ASPECT_CLASSES = 32;
/** How long a presigned upload is valid (B.0's asset store: 15 minutes). */
export const GRAPHICS_UPLOAD_URL_SECONDS = 15 * 60;
/** How long a presigned download is valid (B.0's asset store: 5 minutes). */
export const GRAPHICS_DOWNLOAD_URL_SECONDS = 5 * 60;

/** A project's id: a server ULID, or the designer's own id (a UUID) when it moves a local project to the cloud. */
export const graphicsProjectIdWireSchema = entityIdWireSchema;

export const graphicsProjectPathWireSchema = z.object({ projectId: graphicsProjectIdWireSchema }).strict();

const sceneIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/, 'a scene id is 1-128 letters, digits, hyphens or underscores (no dots: it names files)');

/**
 * gfx's `isSafeAssetPath` (`gfx/packages/gfxpkg/src/assets.ts`, = gfx-schema's `is_safe_asset_path`): package-relative,
 * `/`-separated, no empty, `.` or `..` segment, no `\` or control character, at most 256 scalar values. It is also what
 * keeps `acp graphics projects pull` inside its folder.
 */
export function isSafeGraphicsAssetPath(p: string): boolean {
  if (p === '' || p.startsWith('/')) return false;
  let chars = 0;
  for (const c of p) {
    chars += 1;
    if (c === '\\' || /\p{Cc}/u.test(c)) return false;
  }
  if (chars > MAX_GRAPHICS_ASSET_PATH) return false;
  return p.split('/').every((s) => s !== '' && s !== '.' && s !== '..');
}

const assetPathSchema = z.string().refine(isSafeGraphicsAssetPath, { message: 'an asset path is package-relative, `/`-separated, without empty, `.` or `..` segments (gfx’s isSafeAssetPath)' });

/** A MIME type, `type/subtype`, without parameters. */
const contentTypeSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]{0,63}\/[A-Za-z0-9][A-Za-z0-9!#$&^_.+-]{0,63}$/, 'a content type is `type/subtype`');

export const graphicsSceneWireSchema = z
  .object({
    id: sceneIdSchema,
    name: z.string().min(1).max(200),
    /** The scene JSON's content object. */
    contentSha256: sha256HexWireSchema,
    /** Its sample data track's content object (GFX-D64), or null when it has none. */
    trackSha256: sha256HexWireSchema.nullable(),
  })
  .strict();
export type GraphicsSceneWire = z.infer<typeof graphicsSceneWireSchema>;

export const graphicsAssetWireSchema = z
  .object({
    /** Package-relative path, as the scenes reference it (`assets/fonts/x/X.ttf`). */
    path: assetPathSchema,
    sha256: sha256HexWireSchema,
    size: z.number().int().nonnegative().max(MAX_GRAPHICS_OBJECT_BYTES),
  })
  .strict();
export type GraphicsAssetWire = z.infer<typeof graphicsAssetWireSchema>;

const scenesSchema = z
  .array(graphicsSceneWireSchema)
  .max(MAX_GRAPHICS_SCENES)
  .refine((scenes) => new Set(scenes.map((s) => s.id)).size === scenes.length, { message: 'scene ids must be unique' });

const assetsSchema = z
  .array(graphicsAssetWireSchema)
  .max(MAX_GRAPHICS_ASSETS)
  .refine((assets) => new Set(assets.map((a) => a.path)).size === assets.length, { message: 'asset paths must be unique' })
  .refine((assets) => assets.reduce((sum, a) => sum + a.size, 0) <= MAX_GRAPHICS_PROJECT_ASSET_BYTES, { message: 'a project’s assets are at most 1 GiB together' });

const projectNameSchema = z.string().trim().min(1).max(200);

export const graphicsProjectWireSchema = z
  .object({
    projectId: graphicsProjectIdWireSchema,
    name: z.string(),
    createdAt: isoTimestampWireSchema,
    /** Who created it (a user id), or null when the creator is unknown (a machine writer). */
    createdBy: z.string().nullable(),
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().nullable(),
    /** Bumped by every save; the next save names it as its `baseRevision`. */
    revision: z.number().int().positive(),
    /** In the designer's order. */
    scenes: z.array(graphicsSceneWireSchema),
    assets: z.array(graphicsAssetWireSchema),
  })
  .strict();
export type GraphicsProjectWire = z.infer<typeof graphicsProjectWireSchema>;

export const graphicsProjectSummaryWireSchema = z
  .object({
    projectId: graphicsProjectIdWireSchema,
    name: z.string(),
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().nullable(),
    revision: z.number().int().positive(),
    sceneCount: z.number().int().nonnegative(),
    assetCount: z.number().int().nonnegative(),
  })
  .strict();
export type GraphicsProjectSummaryWire = z.infer<typeof graphicsProjectSummaryWireSchema>;

export const listGraphicsProjectsResponseWireSchema = z.object({ projects: z.array(graphicsProjectSummaryWireSchema) }).strict();
export type ListGraphicsProjectsResponseWire = z.infer<typeof listGraphicsProjectsResponseWireSchema>;

export const createGraphicsProjectRequestWireSchema = z
  .object({
    /** The id to create it under: the designer's own id when it moves a local project to the cloud, or a retry's. Absent: a new ULID. */
    projectId: graphicsProjectIdWireSchema.optional(),
    name: projectNameSchema,
    scenes: scenesSchema,
    assets: assetsSchema,
  })
  .strict();
export type CreateGraphicsProjectRequestWire = z.infer<typeof createGraphicsProjectRequestWireSchema>;

export const saveGraphicsProjectRequestWireSchema = z
  .object({
    /** The revision this edit started from; another save since is `conflict`. */
    baseRevision: z.number().int().positive(),
    name: projectNameSchema,
    scenes: scenesSchema,
    assets: assetsSchema,
  })
  .strict();
export type SaveGraphicsProjectRequestWire = z.infer<typeof saveGraphicsProjectRequestWireSchema>;

/** A presigned S3 request: send `headers` exactly as given (they are part of the signature). */
export const signedContentRequestWireSchema = z
  .object({
    sha256: sha256HexWireSchema,
    method: z.enum(['PUT', 'GET']),
    url: z.url(),
    headers: z.record(z.string(), z.string()),
    expiresAt: isoTimestampWireSchema,
  })
  .strict();
export type SignedContentRequestWire = z.infer<typeof signedContentRequestWireSchema>;

export const graphicsProjectResponseWireSchema = z
  .object({
    project: graphicsProjectWireSchema,
    /** A presigned GET for each content object its scenes name (scene JSON and tracks), valid five minutes. */
    content: z.array(signedContentRequestWireSchema),
  })
  .strict();
export type GraphicsProjectResponseWire = z.infer<typeof graphicsProjectResponseWireSchema>;

export const deleteGraphicsProjectResponseWireSchema = z.object({ projectId: graphicsProjectIdWireSchema, revision: z.number().int().positive() }).strict();
export type DeleteGraphicsProjectResponseWire = z.infer<typeof deleteGraphicsProjectResponseWireSchema>;

export const graphicsUploadObjectWireSchema = z
  .object({
    sha256: sha256HexWireSchema,
    size: z.number().int().nonnegative().max(MAX_GRAPHICS_OBJECT_BYTES),
    contentType: contentTypeSchema,
  })
  .strict();
export type GraphicsUploadObjectWire = z.infer<typeof graphicsUploadObjectWireSchema>;

export const requestGraphicsUploadsRequestWireSchema = z
  .object({ objects: z.array(graphicsUploadObjectWireSchema).min(1).max(MAX_GRAPHICS_OBJECTS_PER_REQUEST) })
  .strict();
export type RequestGraphicsUploadsRequestWire = z.infer<typeof requestGraphicsUploadsRequestWireSchema>;

export const requestGraphicsUploadsResponseWireSchema = z
  .object({
    /** Objects the tenant already has: nothing to send. */
    existing: z.array(sha256HexWireSchema),
    /** A presigned PUT for each missing one, bound to its SHA-256 and size, valid fifteen minutes. */
    uploads: z.array(signedContentRequestWireSchema),
  })
  .strict();
export type RequestGraphicsUploadsResponseWire = z.infer<typeof requestGraphicsUploadsResponseWireSchema>;

export const requestGraphicsDownloadsRequestWireSchema = z.object({ sha256s: z.array(sha256HexWireSchema).min(1).max(MAX_GRAPHICS_OBJECTS_PER_REQUEST) }).strict();
export type RequestGraphicsDownloadsRequestWire = z.infer<typeof requestGraphicsDownloadsRequestWireSchema>;

export const requestGraphicsDownloadsResponseWireSchema = z
  .object({
    downloads: z.array(signedContentRequestWireSchema),
    /** Named objects the tenant does not have (never uploaded and confirmed): no URL. */
    missing: z.array(sha256HexWireSchema),
  })
  .strict();
export type RequestGraphicsDownloadsResponseWire = z.infer<typeof requestGraphicsDownloadsResponseWireSchema>;

/** An aspect ratio as gfx writes one: `16/9`, `1`, `2.39`. */
const aspectSchema = z.string().regex(/^[0-9]{1,6}(?:\.[0-9]{1,6})?(?:\/[0-9]{1,6}(?:\.[0-9]{1,6})?)?$/, 'an aspect is `w/h` or a number');

/** The gfx SDK's `AspectClass` (GFX-D100). */
export const graphicsAspectClassWireSchema = z
  .object({
    id: z.string().regex(/^[A-Za-z_][A-Za-z0-9_-]{0,63}$/, 'a class id is 1-64 letters, digits, hyphens or underscores'),
    name: z.string().min(1).max(64),
    minAspect: aspectSchema.optional(),
    minInclusive: z.boolean().optional(),
    maxAspect: aspectSchema.optional(),
    maxInclusive: z.boolean().optional(),
    preview: aspectSchema.optional(),
  })
  .strict();
export type GraphicsAspectClassWire = z.infer<typeof graphicsAspectClassWireSchema>;

export const graphicsAspectClassesWireSchema = z
  .object({
    /** The set in matching order (first match wins), or null when the tenant never saved one (the designer's defaults apply). */
    classes: z.array(graphicsAspectClassWireSchema).nullable(),
    /** 0 before the first save; the next save names it. */
    version: z.number().int().nonnegative(),
    updatedAt: isoTimestampWireSchema.nullable(),
    updatedBy: z.string().nullable(),
  })
  .strict();
export type GraphicsAspectClassesWire = z.infer<typeof graphicsAspectClassesWireSchema>;

export const updateGraphicsAspectClassesRequestWireSchema = z
  .object({
    /** The version this edit started from (0: the tenant never saved one); another save since is `conflict`. */
    version: z.number().int().nonnegative(),
    classes: z
      .array(graphicsAspectClassWireSchema)
      .min(1)
      .max(MAX_GRAPHICS_ASPECT_CLASSES)
      .refine((classes) => new Set(classes.map((c) => c.id)).size === classes.length, { message: 'class ids must be unique' }),
  })
  .strict();
export type UpdateGraphicsAspectClassesRequestWire = z.infer<typeof updateGraphicsAspectClassesRequestWireSchema>;

// ─── templates and themes (M1 wave 4; GFX-D84, D59) ───

/**
 * A template's or theme's id: gfx's id alphabet (`ID_PATTERN`, `gfx/packages/gfxpkg/src/gfxpkg.ts`) — a theme's id is the
 * one its `theme.json` names, a template's the designer's.
 */
export const graphicsLibraryIdWireSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/, 'an id is 1-128 letters, digits, hyphens or underscores (gfx’s ID_PATTERN)');
/** A version number: 1 … 999 999 999. */
export const graphicsVersionWireSchema = z.number().int().min(1).max(999_999_999);
/** A version number in a path, as its decimal text (the handler reads it with `Number`). */
const versionPathSchema = z.string().regex(/^[1-9][0-9]{0,8}$/, 'a version is a positive whole number (1-999999999)');

/** The `.gfxpkg` media type (gfx's `GFXPKG_MIME`): what a template package is uploaded as. */
export const GFXPKG_CONTENT_TYPE = 'application/vnd.gfx.package+zip';
/** Most versions of one theme the library keeps: a theme's delete is one transaction of these and the theme (DynamoDB's 100 items, one for the log). */
export const MAX_GRAPHICS_THEME_VERSIONS = 98;

const libraryNameSchema = z.string().trim().min(1).max(200);
const noteSchema = z.string().trim().max(2000);

export const graphicsTemplatePathWireSchema = z.object({ templateId: graphicsLibraryIdWireSchema }).strict();
export const graphicsTemplateVersionPathWireSchema = z.object({ templateId: graphicsLibraryIdWireSchema, version: versionPathSchema }).strict();
export const graphicsThemePathWireSchema = z.object({ themeId: graphicsLibraryIdWireSchema }).strict();
export const graphicsThemeVersionPathWireSchema = z.object({ themeId: graphicsLibraryIdWireSchema, version: versionPathSchema }).strict();

/** One Mimir graphics item made from a template version (ACP-D48): its Mimir id and when it was made. */
export const graphicsMimirItemWireSchema = z.object({ itemId: z.string().min(1), at: isoTimestampWireSchema }).strict();
export type GraphicsMimirItemWire = z.infer<typeof graphicsMimirItemWireSchema>;

/**
 * A published template version (GFX-D59: "a scene published for fill-in"): an immutable `.gfxpkg` content object holding
 * the scene (gfx's `packScene`, the package D96's Mimir upload is), named `template@sha256` (D84's pin). Never changed
 * after it is published; a template's delete leaves its versions readable by reference.
 */
export const graphicsTemplateVersionWireSchema = z
  .object({
    templateId: graphicsLibraryIdWireSchema,
    version: graphicsVersionWireSchema,
    /** `<templateId>@<packageSha256>`: what a cue or a render job pins (D84). */
    ref: z.string(),
    packageSha256: sha256HexWireSchema,
    size: z.number().int().nonnegative(),
    /** The template's name when this version was published. */
    name: z.string(),
    /** The scene the package holds (its id in the project it was published from). */
    sceneId: z.string(),
    /** The project and revision it was published from, or null (published from a file). */
    sourceProjectId: z.string().nullable(),
    sourceRevision: z.number().int().positive().nullable(),
    /** What changed, in the publisher's words. */
    note: z.string().nullable(),
    publishedAt: isoTimestampWireSchema,
    publishedBy: z.string().nullable(),
    /** The Mimir graphics items made from this version (ACP-D48: a new one per Add to Mimir), oldest first. */
    mimirItems: z.array(graphicsMimirItemWireSchema),
  })
  .strict();
export type GraphicsTemplateVersionWire = z.infer<typeof graphicsTemplateVersionWireSchema>;

export const graphicsTemplateWireSchema = z
  .object({
    templateId: graphicsLibraryIdWireSchema,
    name: z.string(),
    description: z.string().nullable(),
    latestVersion: graphicsVersionWireSchema,
    /** The project and scene the latest version was published from (where the designer offers to publish the next one). */
    sourceProjectId: z.string().nullable(),
    sourceSceneId: z.string().nullable(),
    createdAt: isoTimestampWireSchema,
    createdBy: z.string().nullable(),
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().nullable(),
    /** Bumped by every publish (what the change channel announces). */
    revision: z.number().int().positive(),
  })
  .strict();
export type GraphicsTemplateWire = z.infer<typeof graphicsTemplateWireSchema>;

export const listGraphicsTemplatesResponseWireSchema = z.object({ templates: z.array(graphicsTemplateWireSchema) }).strict();
export type ListGraphicsTemplatesResponseWire = z.infer<typeof listGraphicsTemplatesResponseWireSchema>;

export const graphicsTemplateResponseWireSchema = z
  .object({
    template: graphicsTemplateWireSchema,
    /** Every version, newest first. */
    versions: z.array(graphicsTemplateVersionWireSchema),
  })
  .strict();
export type GraphicsTemplateResponseWire = z.infer<typeof graphicsTemplateResponseWireSchema>;

export const publishGraphicsTemplateRequestWireSchema = z
  .object({
    /** The template's name from this version on. */
    name: libraryNameSchema,
    /** Its description from this version on; absent keeps the template's (none for a new one). */
    description: noteSchema.nullable().optional(),
    /** The package, uploaded first (`POST /graphics/content/uploads`, content type `application/vnd.gfx.package+zip`). */
    packageSha256: sha256HexWireSchema,
    size: z.number().int().positive().max(MAX_GRAPHICS_OBJECT_BYTES),
    sceneId: sceneIdSchema,
    sourceProjectId: graphicsProjectIdWireSchema.nullable(),
    sourceRevision: z.number().int().positive().nullable(),
    note: noteSchema.nullable(),
  })
  .strict();
export type PublishGraphicsTemplateRequestWire = z.infer<typeof publishGraphicsTemplateRequestWireSchema>;

export const publishGraphicsTemplateResponseWireSchema = z.object({ template: graphicsTemplateWireSchema, version: graphicsTemplateVersionWireSchema }).strict();
export type PublishGraphicsTemplateResponseWire = z.infer<typeof publishGraphicsTemplateResponseWireSchema>;

export const graphicsTemplateVersionResponseWireSchema = z
  .object({
    version: graphicsTemplateVersionWireSchema,
    /** A presigned GET of the package, valid five minutes. */
    download: signedContentRequestWireSchema,
  })
  .strict();
export type GraphicsTemplateVersionResponseWire = z.infer<typeof graphicsTemplateVersionResponseWireSchema>;

export const deleteGraphicsTemplateResponseWireSchema = z.object({ templateId: graphicsLibraryIdWireSchema, revision: z.number().int().positive() }).strict();
export type DeleteGraphicsTemplateResponseWire = z.infer<typeof deleteGraphicsTemplateResponseWireSchema>;

/**
 * One version of a theme package (D84) in the tenant's library: its `theme.json` as a content object (the theme's
 * identity is that text's SHA-256: the designer's `digest`) and its asset files. Unlike a template version it is the
 * designer's working copy, saved in place: `revision` is its optimistic-concurrency counter.
 */
export const graphicsThemeVersionWireSchema = z
  .object({
    themeId: graphicsLibraryIdWireSchema,
    version: graphicsVersionWireSchema,
    name: z.string(),
    /** `theme.json`'s content object (byte for byte as packaged). */
    themeSha256: sha256HexWireSchema,
    assets: z.array(graphicsAssetWireSchema),
    revision: z.number().int().positive(),
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().nullable(),
  })
  .strict();
export type GraphicsThemeVersionWire = z.infer<typeof graphicsThemeVersionWireSchema>;

export const graphicsThemeWireSchema = z
  .object({
    themeId: graphicsLibraryIdWireSchema,
    /** The latest version's name. */
    name: z.string(),
    latestVersion: graphicsVersionWireSchema,
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().nullable(),
    /** Bumped by every write to the theme or one of its versions (what the change channel announces). */
    revision: z.number().int().positive(),
    /** Its versions, oldest first. */
    versions: z.array(graphicsThemeVersionWireSchema),
  })
  .strict();
export type GraphicsThemeWire = z.infer<typeof graphicsThemeWireSchema>;

export const listGraphicsThemesResponseWireSchema = z.object({ themes: z.array(graphicsThemeWireSchema) }).strict();
export type ListGraphicsThemesResponseWire = z.infer<typeof listGraphicsThemesResponseWireSchema>;

export const graphicsThemeResponseWireSchema = z
  .object({
    theme: graphicsThemeWireSchema,
    /** A presigned GET (valid five minutes) of each version's `theme.json`. */
    content: z.array(signedContentRequestWireSchema),
  })
  .strict();
export type GraphicsThemeResponseWire = z.infer<typeof graphicsThemeResponseWireSchema>;

export const saveGraphicsThemeVersionRequestWireSchema = z
  .object({
    /** The version's revision this edit started from, or null to add the version (refused when it exists). */
    baseRevision: z.number().int().positive().nullable(),
    name: libraryNameSchema,
    themeSha256: sha256HexWireSchema,
    assets: assetsSchema,
  })
  .strict();
export type SaveGraphicsThemeVersionRequestWire = z.infer<typeof saveGraphicsThemeVersionRequestWireSchema>;

export const saveGraphicsThemeVersionResponseWireSchema = z.object({ theme: graphicsThemeWireSchema, version: graphicsThemeVersionWireSchema }).strict();
export type SaveGraphicsThemeVersionResponseWire = z.infer<typeof saveGraphicsThemeVersionResponseWireSchema>;

export const deleteGraphicsThemeResponseWireSchema = z
  .object({
    themeId: graphicsLibraryIdWireSchema,
    /** The theme as it is left, or null when no version remains (the theme is gone). */
    theme: graphicsThemeWireSchema.nullable(),
  })
  .strict();
export type DeleteGraphicsThemeResponseWire = z.infer<typeof deleteGraphicsThemeResponseWireSchema>;
